<script setup lang="ts">
import type { AccountAssist } from '@support/client';

/**
 * 客服申请登录客户账号：申请中显示同意/拒绝，授权中显示正在排查与取消授权。
 * variant：bar 聊天页顶部的提示条，card 首页卡片；两处统一使用警示色提醒客户暂停操作账套。
 */
withDefaults(defineProps<{ assist: AccountAssist; busy: boolean; deadline: string; error: string; variant?: 'bar' | 'card' }>(), {
  variant: 'bar',
});
const emit = defineEmits<{ act: [action: 'accept' | 'reject' | 'revoke'] }>();
</script>

<template>
  <div class="assist-notice" :class="`assist-notice--${variant}`" data-testid="widget-assist" role="status">
    <svg class="assist-notice__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M10.3 4.9a2 2 0 0 1 3.4 0l7 12.1a2 2 0 0 1-1.7 3H5a2 2 0 0 1-1.7-3l7-12.1Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" />
      <path d="M12 9v4" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
      <circle cx="12" cy="16.5" r="1" fill="currentColor" />
    </svg>
    <p class="assist-notice__text">
      <template v-if="assist.status === 'pending'">人工客服申请临时登录您的账号，协助排查账套问题。同意后最长 8 小时有效，期间可随时取消授权。</template>
      <template v-else-if="assist.openedAt">人工客服正在为您进账套排查问题，<strong>请勿操作相关账套。</strong></template>
      <template v-else>已授权人工客服登录您的账号排查问题，期间请勿操作相关账套。</template>
      <span v-if="assist.status !== 'pending' && deadline" class="assist-notice__meta">授权至 {{ deadline }}</span>
    </p>
    <div class="assist-notice__actions">
      <template v-if="assist.status === 'pending'">
        <button class="assist-notice__btn" type="button" data-testid="widget-assist-reject" :disabled="busy" @click="emit('act', 'reject')">拒绝</button>
        <button class="assist-notice__btn assist-notice__btn--primary" type="button" data-testid="widget-assist-accept" :disabled="busy" @click="emit('act', 'accept')">同意授权</button>
      </template>
      <button v-else class="assist-notice__btn" type="button" data-testid="widget-assist-revoke" :disabled="busy" @click="emit('act', 'revoke')">取消授权</button>
    </div>
    <p v-if="error" class="assist-notice__error" role="alert">{{ error }}</p>
  </div>
</template>

<style scoped>
.assist-notice {
  /* 授权期间涉及账套操作，使用独立的橙色警示层级，避免与普通白色卡片混淆。 */
  --assist-bg: #fffbeb;
  --assist-border: #f5cf8e;
  --assist-accent: #b45309;
  --assist-text: #92400e;
  box-sizing: border-box;
  color: var(--assist-text);
  background: var(--assist-bg);
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr);
  flex: none;
  align-items: start;
  gap: 10px;
  font-size: 12px;
}

/* 聊天页保持紧凑提示条，警示色与首页一致。 */
.assist-notice--bar {
  border-bottom: 1px solid var(--assist-border);
  border-left: 4px solid var(--assist-accent);
  padding: 10px 12px;
}

/* 首页保留原有卡片尺寸与按钮位置，用左侧色条强化持续授权的提醒。 */
.assist-notice--card {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid var(--assist-border);
  border-left: 4px solid var(--assist-accent);
  border-radius: var(--w-radius-lg);
  box-shadow: 0 4px 12px -6px rgb(146 64 14 / 18%);
  font-size: 13px;
}

.assist-notice__icon {
  width: 20px;
  height: 20px;
  color: var(--assist-accent);
}

.assist-notice__text {
  min-width: 0;
  margin: 0;
  line-height: 1.65;
  overflow-wrap: anywhere;
}

.assist-notice__text strong {
  font-weight: 600;
}

.assist-notice__meta {
  display: block;
  margin-top: 4px;
  color: #9a6324;
  font-size: 12px;
}

.assist-notice__actions {
  grid-column: 2;
  justify-self: end;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.assist-notice__btn {
  background: var(--w-bg);
  border: 1px solid var(--assist-border);
  border-radius: var(--w-radius);
  color: var(--assist-text);
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  padding: 5px 12px;
  white-space: nowrap;
  transition: border-color var(--w-dur-fast) var(--w-ease), color var(--w-dur-fast) var(--w-ease),
    background-color var(--w-dur-fast) var(--w-ease);
}

.assist-notice__btn:hover:not(:disabled) {
  background: #fff3d6;
  border-color: var(--assist-accent);
  color: var(--assist-text);
}

/* 同意授权沿用警示色，避免与普通咨询的青色主按钮混淆。 */
.assist-notice__btn--primary {
  background: var(--assist-accent);
  border-color: var(--assist-accent);
  color: var(--w-on-primary);
  font-weight: 600;
}

.assist-notice__btn--primary:hover:not(:disabled) {
  background: #92400e;
  border-color: #92400e;
  color: var(--w-on-primary);
}

.assist-notice__btn:focus-visible {
  outline: 2px solid var(--assist-accent);
  outline-offset: 3px;
}

.assist-notice__btn:disabled {
  cursor: wait;
  opacity: 0.6;
}

.assist-notice__error {
  grid-column: 2;
  margin: 0;
  color: var(--w-danger);
}

@media (prefers-reduced-motion: reduce) {
  .assist-notice__btn {
    transition: none;
  }
}
</style>
